import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';

const SignupForm = ({ selectedRole }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/?.test(formData?.password)) {
      newErrors.password = 'Password must include uppercase, lowercase, number, and special character';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    // Ensure role is valid, fallback to influencer
    const role = (selectedRole === 'brand' || selectedRole === 'influencer' || selectedRole === 'admin')
      ? selectedRole
      : 'influencer';

    console.debug('[SignupForm] Submitting signup for:', formData?.email, 'role:', role);

    try {
      const { data, error } = await signUp(formData?.email, formData?.password, {
        full_name: formData?.fullName?.trim(),
        role,
      });

      console.debug('[SignupForm] signUp result — data:', data, 'error:', error);

      if (error) {
        const message = error?.message || 'Failed to create account. Please try again.';
        console.error('[SignupForm] Signup error:', message);
        setErrors({ submit: message });
        setLoading(false);
        return;
      }

      // If session is null, Supabase requires email confirmation
      if (!data?.session) {
        console.debug('[SignupForm] Email confirmation required — showing confirmation screen');
        setEmailConfirmationSent(true);
        setLoading(false);
        return;
      }

      // Session available immediately (email confirmation disabled) — redirect by role
      console.debug('[SignupForm] Session available — redirecting to profile verification');
      navigate('/profile-verification-screen');
    } catch (err) {
      console.error('[SignupForm] Unexpected error during signup:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
      setLoading(false);
    }
  };

  if (emailConfirmationSent) {
    return (
      <div className="space-y-5">
        <div className="p-6 rounded-xl bg-success/10 border border-success/20 text-center">
          <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-foreground font-semibold text-lg mb-1">Check your email</h3>
          <p className="text-muted-foreground text-sm">
            We sent a confirmation link to <span className="text-foreground font-medium">{formData?.email}</span>.
            Click the link to activate your account and sign in.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEmailConfirmationSent(false)}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
        >
          ← Back to sign up
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Full Name"
        type="text"
        name="fullName"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
      />
      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />
      <Input
        label="Password"
        type="password"
        name="password"
        placeholder="Create a strong password"
        description="Must be 8+ characters with uppercase, lowercase, number, and special character"
        value={formData?.password}
        onChange={handleChange}
        error={errors?.password}
        required
      />
      <Input
        label="Confirm Password"
        type="password"
        name="confirmPassword"
        placeholder="Re-enter your password"
        value={formData?.confirmPassword}
        onChange={handleChange}
        error={errors?.confirmPassword}
        required
      />
      <Checkbox
        label={
          <span className="text-sm">
            I agree to the{' '}
            <button type="button" className="text-primary hover:text-primary/80">
              Terms of Service
            </button>
            {' '}and{' '}
            <button type="button" className="text-primary hover:text-primary/80">
              Privacy Policy
            </button>
          </span>
        }
        name="agreeToTerms"
        checked={formData?.agreeToTerms}
        onChange={handleChange}
        error={errors?.agreeToTerms}
        required
      />
      {errors?.submit && (
        <div className="p-4 rounded-lg bg-error/10 border border-error/20">
          <p className="text-sm text-error">{errors?.submit}</p>
        </div>
      )}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        iconName="UserPlus"
        iconPosition="right"
        className="h-12"
      >
        Create Account
      </Button>
    </form>
  );
};

export default SignupForm;